/**
 * 지출결의등록(type1) — 결의/임시묶음 상세 (과목별 증빙·채주)
 */

import React, { useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import DraggableDialog from 'components/common/DraggableDialog';
import { ActionButton } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { HiGridComponent, hiCol } from 'components/grid/hiGrid';
import { TYPE1_DRAFT_NO_LABEL } from 'expense/resolutionType1DraftStore';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  min-height: 0;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.primary};
  padding: 4px 2px;
  flex-shrink: 0;
  line-height: 1.5;
`;

const BundleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-height: 0;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const BundleHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 6px 10px;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  background: ${theme.colors.background.light};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const BundleGrid = styled.div`
  flex: 1;
  min-height: 120px;
  display: flex;
  flex-direction: column;
`;

const ScrollBundles = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FooterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
  padding-top: 4px;
  border-top: 1px solid ${theme.colors.border};
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

function formatAmt(n) {
  return Number(n || 0).toLocaleString();
}

function BundlePayeeGrid({ rows, prefsUserId, gridId }) {
  const ref = useRef(null);
  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '채주명', field: 'vendorName', size: 120 }),
      hiCol.roTextColumn({ header: '증빙유형', field: 'evidenceType', size: 90 }),
      hiCol.roTextColumn({ header: '증빙번호', field: 'evidenceNo', size: 110 }),
      hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '부가세', field: 'vatYn', size: 60, align: 'center' }),
      hiCol.roNumberColumn({ header: '부가세액', field: 'vatAmt', size: 90, align: 'right' }),
      hiCol.roTextColumn({ header: '카드', field: 'cardNo', size: 120 }),
      hiCol.roTextColumn({ header: '은행', field: 'bankName', size: 90 }),
      hiCol.roTextColumn({ header: '계좌', field: 'accountNo', size: 120 }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 140 }),
    ],
    []
  );

  useEffect(() => {
    const t = setTimeout(() => {
      ref.current?.setData?.(rows || []);
    }, 0);
    return () => clearTimeout(t);
  }, [rows]);

  return (
    <BundleGrid>
      <HiGridComponent
        ref={ref}
        columns={columns}
        height="100%"
        showRowNumber
        rowNumberSize={48}
        enableColumnFilters
        enableHeaderMenu
        enableSelectionStats
        enableRowCheck={false}
        enableQuickExport
        exportFileName="결의상세-증빙"
        gridId={gridId}
        prefsUserId={prefsUserId}
      />
    </BundleGrid>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object|null} props.master 결의 마스터 또는 임시행
 * @param {{ subjectCode: string, subjectName: string, applyAmt?: number, payees: object[] }[]} props.bundles
 * @param {string} [props.prefsUserId]
 */
export default function ExpenseResolutionType1DetailDialog({
  open,
  onClose,
  master,
  bundles = [],
  prefsUserId = '',
}) {
  const titleNo = master?.isDraft
    ? TYPE1_DRAFT_NO_LABEL
    : master?.resolutionNo || master?.resolutionUid || '';

  const totalAmt = (bundles || []).reduce(
    (s, b) =>
      s +
      (b.payees || [])
        .filter((p) => p.mod !== 'D')
        .reduce((a, p) => a + (Number(p.amount) || 0), 0),
    0
  );

  return (
    <DraggableDialog
      open={open}
      title={`결의 상세 · ${titleNo}`}
      onClose={onClose}
      dialogWidth="min(96vw, 1080px)"
      dialogHeight="min(88vh, 720px)"
      prefsUserId={prefsUserId}
      sizeKey="expense-res-t1-detail"
      keepMounted={false}
    >
      <Body>
        <Summary>
          <span>
            <strong>결의번호</strong> {titleNo}
          </span>
          <span>
            <strong>부서</strong> {master?.deptName || master?.deptCode || '—'}
          </span>
          <span>
            <strong>사업</strong>{' '}
            {master?.projectName
              ? `${master.projectCode || ''} ${master.projectName}`.trim()
              : master?.projectCode || '—'}
          </span>
          <span>
            <strong>과목수</strong> {(bundles || []).length}개
          </span>
          <span>
            <strong>금액합</strong> {formatAmt(totalAmt)}원
          </span>
          {master?.statusNm ? (
            <span>
              <strong>상태</strong> {master.statusNm}
            </span>
          ) : null}
        </Summary>

        <ScrollBundles>
          {!(bundles || []).length ? (
            <Empty>연결된 과목·증빙 내역이 없습니다.</Empty>
          ) : (
            (bundles || []).map((b, idx) => {
              const payees = (b.payees || []).filter((p) => p.mod !== 'D');
              const subAmt =
                Number(b.applyAmt) ||
                payees.reduce((s, p) => s + (Number(p.amount) || 0), 0);
              return (
                <BundleBlock key={b.bundleUid || `${b.subjectCode}-${idx}`}>
                  <BundleHead>
                    <span>
                      과목 {b.subjectCode || '—'} · {b.subjectName || ''}
                    </span>
                    <span style={{ fontWeight: 500, color: theme.colors.text.secondary }}>
                      증빙 {payees.length}건 · {formatAmt(subAmt)}원
                    </span>
                  </BundleHead>
                  <BundlePayeeGrid
                    rows={payees}
                    prefsUserId={prefsUserId}
                    gridId={`expense-res-t1-detail-${idx}`}
                  />
                </BundleBlock>
              );
            })
          )}
        </ScrollBundles>

        <FooterBar>
          <ActionButton type="button" onClick={onClose}>
            닫기
          </ActionButton>
        </FooterBar>
      </Body>
    </DraggableDialog>
  );
}

/**
 * 결의 마스터+디테일 → 과목별 묶음 (상세 팝업용)
 */
export function buildDetailBundlesFromResolution(master, details = []) {
  const resUid = String(master?.resolutionUid || '').trim();
  const dets = (details || []).filter(
    (d) => d.mod !== 'D' && String(d.resolutionUid || '').trim() === resUid
  );
  const lines = Array.isArray(master?.subjectLines) ? master.subjectLines : [];
  const bySubject = new Map();

  const pushPayee = (code, name, payee, applyAmt) => {
    const key = String(code || '').trim() || '_NONE_';
    if (!bySubject.has(key)) {
      bySubject.set(key, {
        subjectCode: code || '',
        subjectName: name || '',
        applyAmt: Number(applyAmt) || 0,
        payees: [],
      });
    }
    const bucket = bySubject.get(key);
    if (payee) bucket.payees.push(payee);
    if (applyAmt && !bucket.applyAmt) bucket.applyAmt = Number(applyAmt) || 0;
  };

  lines.forEach((line) => {
    pushPayee(line.subjectCode, line.subjectName, null, line.applyAmt);
  });

  dets.forEach((d) => {
    const code = String(d.subjectCode || '').trim();
    if (code) {
      pushPayee(code, d.subjectName || '', d, 0);
      return;
    }
    if (lines.length === 1) {
      pushPayee(lines[0].subjectCode, lines[0].subjectName, d, lines[0].applyAmt);
      return;
    }
    pushPayee('', '미지정 과목', d, 0);
  });

  const ordered = [];
  const used = new Set();
  lines.forEach((line) => {
    const key = String(line.subjectCode || '').trim() || '_NONE_';
    const hit = bySubject.get(key);
    if (hit) {
      if (!hit.applyAmt) {
        hit.applyAmt =
          Number(line.applyAmt) ||
          hit.payees.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      }
      ordered.push(hit);
      used.add(key);
    }
  });
  bySubject.forEach((v, k) => {
    if (!used.has(k)) {
      if (!v.applyAmt) {
        v.applyAmt = v.payees.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      }
      ordered.push(v);
    }
  });
  return ordered;
}

/** 임시세션 → 상세 묶음 */
export function buildDetailBundlesFromDraft(session) {
  return (session?.bundles || [])
    .filter((b) => b && b.mod !== 'D')
    .map((b) => ({
      bundleUid: b.bundleUid,
      subjectCode: b.subjectCode || '',
      subjectName: b.subjectName || '',
      applyAmt: Number(b.applyAmt) || 0,
      payees: (b.payees || []).filter((p) => p.mod !== 'D'),
    }));
}
